import { Link } from "react-router-dom";
import { PieChart } from "@mui/x-charts/PieChart";
import { hasRole } from "../../../components/Utils";
import { OvalLoader } from "../../../components/common/Loader";
import { CHART_PALETTE } from "../../../constant/theme";

const currencyFormatter = (val) => {
  const value = val?.value !== undefined ? val.value : val;
  if (value == null) return "";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value)}`;
};

const countFormatter = (val) => {
  const value = val?.value !== undefined ? val.value : val;
  if (value == null) return "";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value);
};

const ChapterPieCard = ({
  title,
  subtitle,
  loading,
  data,
  total,
  valueFormatter,
  reportLink,
  permission,
  hasPermission,
}) => {
  const canViewReport =
    reportLink && (!permission || !hasPermission || hasPermission(permission));

  const topValue = data.reduce((max, item) => Math.max(max, item.value), 0);
  const centerPct = total > 0 ? Math.round((topValue / total) * 100) : 0;

  return (
    <div className="chart-card chapter-pie-card">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-card-title">{title}</h3>
          <p className="chart-card-subtitle-text">{subtitle}</p>
        </div>
      </div>
      <div className="chart-card-body">
        {loading ? (
          <div className="chart-loader">
            <OvalLoader color="#1E3A93" />
          </div>
        ) : data.length > 0 ? (
          <div className="chapter-pie-layout">
            <div className="chapter-pie-donut">
              <PieChart
                colors={CHART_PALETTE}
                series={[
                  {
                    data,
                    innerRadius: 58,
                    outerRadius: 78,
                    paddingAngle: 2,
                    cornerRadius: 6,
                    valueFormatter,
                  },
                ]}
                width={168}
                height={168}
                margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
                slotProps={{ legend: { hidden: true } }}
              />
              <div className="chapter-pie-center">{centerPct}%</div>
            </div>
            <div className="chapter-pie-legend">
              {data.map((item, index) => {
                const pct =
                  total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
                return (
                  <div className="chapter-pie-legend-item" key={item.id}>
                    <span
                      className="chapter-pie-swatch"
                      style={{
                        background: CHART_PALETTE[index % CHART_PALETTE.length],
                      }}
                    />
                    <span>
                      <span className="chapter-pie-name">{item.label}</span>
                      <span className="chapter-pie-pct">{pct}%</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="chart-empty">No data available</div>
        )}
      </div>
      {canViewReport && (
        <Link to={reportLink} className="chapter-pie-link">
          <span>View Report</span>
          <i className="bx bx-right-arrow-alt"></i>
        </Link>
      )}
    </div>
  );
};

const ChapterWiseCharts = ({ data, ovalLoading, hasPermission }) => {
  if (!hasPermission("dashboard-tyns-data-view") || hasRole([3])) {
    return null;
  }

  const chartDataSet3 = (data.chapterWiseBusiness || [])
    .filter((item) => item.amount > 0)
    .map((item, index) => ({
      id: index,
      value: item.amount,
      label: item.chapter_name,
    }));

  const chartDataSet4 = (data.chapterWiseOneToOne || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet5 = (data.chapterWiseReferrals || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const chartDataSet6 = (data.chapterWiseVisitors || [])
    .filter((item) => item.count > 0)
    .map((item, index) => ({
      id: index,
      value: item.count,
      label: item.chapter_name,
    }));

  const totalBiz = chartDataSet3.reduce((sum, item) => sum + item.value, 0);
  const total121s = chartDataSet4.reduce((sum, item) => sum + item.value, 0);
  const totalRef = chartDataSet5.reduce((sum, item) => sum + item.value, 0);
  const totalVis = chartDataSet6.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="pie-charts-grid mb-4">
      <ChapterPieCard
        title="Chapter Wise Business"
        subtitle="Business generated by each chapter"
        loading={ovalLoading}
        data={chartDataSet3}
        total={totalBiz}
        valueFormatter={currencyFormatter}
        reportLink="/reports/biz-given"
        permission="reports-chapter-wise-business-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise 121s"
        subtitle="121s completed by each chapter"
        loading={ovalLoading}
        data={chartDataSet4}
        total={total121s}
        valueFormatter={countFormatter}
        reportLink="/reports/one-to-one"
        permission="reports-chapter-wise-one-to-one-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise Referrals"
        subtitle="Referrals given by each chapter"
        loading={ovalLoading}
        data={chartDataSet5}
        total={totalRef}
        valueFormatter={countFormatter}
        reportLink="/reports/referrals"
        permission="reports-chapter-wise-referrals-list"
        hasPermission={hasPermission}
      />
      <ChapterPieCard
        title="Chapter Wise Visitors"
        subtitle="Visitors brought by each chapter"
        loading={ovalLoading}
        data={chartDataSet6}
        total={totalVis}
        valueFormatter={countFormatter}
        reportLink="/reports/visitors"
        permission="reports-chapter-wise-visitors-list"
        hasPermission={hasPermission}
      />
    </div>
  );
};

export default ChapterWiseCharts;
